/**
 * StocktakeListPage — the thin wrapper for the converged stocktake list
 * (#1418). The list body (columns, pagination, row-aware count/review
 * dispatch, empty/error states, CSV toolbar) is fully described by
 * `stocktakeResource`; this component supplies the one thing the framework
 * has no slot for: the "open new stocktake for this week" CTA (a DatePicker +
 * open mutation). It composes its own `PageContainer` and renders
 * `ResourcePage` with `container={false}`, so the two share one app frame —
 * the StockLevels / WebhookEvents wrapper pattern.
 *
 * Permission gate is route-level (`STOCKTAKE_READ` in `App.tsx`); the open CTA
 * additionally checks the operator's grant client-side before rendering — the
 * server-side guard on `POST /api/stocktakes` (`stocktake:write`) remains the
 * load-bearing check.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  Card,
  CardContent,
  PageContainer,
  ResourcePage,
  Button,
  DatePicker,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { useAuth } from '@hooks/useAuth';
import { NavGroupTabs } from '@components/layout/NavGroupTabs';
import { stocktakeResource } from '../stocktakeResource';
import { useOpenStocktake } from '../hooks/useStocktake';
import { useToday } from '@resource-kit/controllers/react';
import { isoMondayOf } from '@lib/calendar-day';

/** Heuristic for "today's Monday" (the week_start the operator most likely
 *  wants to open): the ISO Monday of the merchant calendar day `today`, by
 *  pure calendar arithmetic (#2875). `today` comes from `useToday()` — the
 *  merchant time zone, never the browser clock. Returns `YYYY-MM-DD`; the
 *  backend pins it as `week_start` verbatim (no TZ conversion). Operator can
 *  always override. */
export function defaultWeekStart(today: string): string {
  return isoMondayOf(today);
}

export function StocktakeListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const perms: readonly string[] = user?.permissions ?? [];
  // Parity with the bespoke page: the CTA gate reads `ingredients:write`.
  // The wrong key (should be `stocktake:write`) is the SEPARATE defect #1329;
  // this convergence preserves the gate verbatim rather than smuggling in a
  // permission fix.
  const canWrite = perms.includes('ingredients:write');

  const openMutation = useOpenStocktake();
  const today = useToday();
  const [newWeekStart, setNewWeekStart] = useState(() => defaultWeekStart(today));

  const handleOpen = async () => {
    try {
      const res = await openMutation.mutateAsync({ week_start: newWeekStart });
      toast.success(t('stocktake.list.openSuccess', { week: newWeekStart }));
      navigate(`/stocktake/${encodeURIComponent(res.id)}/count`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <PageContainer data-testid="stocktake-list-page">
      {canWrite && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1">
                <span className="text-xs uppercase text-muted-foreground">
                  {t('stocktake.list.openWeekLabel')}
                </span>
                <DatePicker
                  value={newWeekStart}
                  onChange={setNewWeekStart}
                  className="h-12 w-44"
                  data-testid="stocktake-week-start-input"
                />
              </div>
              <Button
                className="h-12 px-6"
                onClick={handleOpen}
                disabled={!newWeekStart || openMutation.isPending}
                data-testid="stocktake-open-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('stocktake.list.openButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <ResourcePage
        bundle={{
          ...stocktakeResource,
          host: {
            ...stocktakeResource.host,
            listTabs: () => <NavGroupTabs group="ingredientsStock" />,
          },
        }}
        container={false}
      />
    </PageContainer>
  );
}

export default StocktakeListPage;
